import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Search, Shield, Lightbulb, Plane, MapPin } from "lucide-react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead, BreadcrumbSchema, ArticleSchema } from "@/components/SEOHead";
import { Skeleton } from "@/components/ui/skeleton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const strictnessConfig = {
  high: { color: "text-red-600", bg: "bg-red-50", label: "HIGH" },
  medium: { color: "text-yellow-700", bg: "bg-yellow-50", label: "MEDIUM" },
  low: { color: "text-green-600", bg: "bg-green-50", label: "LOW" },
};

export default function CountryGuide() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGuide = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/country-guides`);
        const all = res.data;
        const found = all.find(
          (g) =>
            g.country_name?.toLowerCase().replace(/\s+/g, "-") === slug?.toLowerCase() ||
            g.country_code?.toLowerCase() === slug?.toLowerCase()
        );
        setGuide(found || null);
        if (found) {
          setRelated(all.filter((g) => g.country_name !== found.country_name && g.active !== false).slice(0, 4));
        }
      } catch {
        setGuide(null);
      }
      setLoading(false);
    };
    fetchGuide();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 space-y-6">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <MapPin className="w-12 h-12 text-[#d1d5db] mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#1e293b] mb-2">Country Guide Not Found</h1>
          <p className="text-sm text-[#64748b] mb-6">We couldn't find a guide for this country.</p>
          <button onClick={() => navigate("/")} className="bg-[#1e3a5f] text-white px-6 py-2.5 rounded-xl text-sm font-medium">
            Go Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const sc = strictnessConfig[guide.strictness_level] || strictnessConfig.medium;

  return (
    <div className="min-h-screen bg-white" data-testid="country-guide-page">
      <SEOHead
        title={`Onward Ticket for ${guide.country_name} | Requirements & Tips | OnwardFlighTickets`}
        description={guide.description || `Learn about onward ticket requirements for ${guide.country_name}. Get your verified flight reservation from $12.`}
        path={`/guides/${slug}`}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://onwardflightickets.com" },
          { name: "Country Guides", url: "https://onwardflightickets.com/#country-guides" },
          { name: guide.country_name, url: `https://onwardflightickets.com/guides/${slug}` },
        ]}
      />
      <ArticleSchema
        title={`Onward Ticket Requirements for ${guide.country_name}`}
        description={guide.description}
        url={`https://onwardflightickets.com/guides/${slug}`}
      />
      <Header />

      <main className="max-w-3xl mx-auto px-4 py-12 lg:py-16">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">{guide.flag_emoji}</span>
            <div>
              <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#1e293b]" data-testid="guide-country-name">
                {guide.country_name}
              </h1>
              <p className="text-sm text-[#64748b]">{t("guide.requirements")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Shield className="w-4 h-4 text-[#64748b]" />
            <span className="text-xs font-medium text-[#64748b] uppercase">{t("guide.strictness")}:</span>
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${sc.bg} ${sc.color}`}>
              {sc.label}
            </span>
          </div>
        </div>

        {/* Description */}
        {guide.description && (
          <div className="bg-[#f8fafc] rounded-2xl p-6 mb-6">
            <p className="text-sm text-[#475569] leading-relaxed">{guide.description}</p>
          </div>
        )}

        {/* Tips */}
        {guide.tips?.length > 0 && guide.tips.some((t) => t) && (
          <div className="mb-6">
            <h2 className="font-['Outfit'] text-lg font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-[#f97316]" />
              {t("guide.tips_title")} {guide.country_name}
            </h2>
            <ul className="space-y-2">
              {guide.tips.filter(Boolean).map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#475569]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#f97316] mt-1.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Popular routes */}
        {guide.popular_routes?.length > 0 && guide.popular_routes.some((r) => r) && (
          <div className="mb-6">
            <h2 className="font-['Outfit'] text-lg font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
              <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
              {t("guide.popular_routes")}
            </h2>
            <div className="space-y-2">
              {guide.popular_routes.filter(Boolean).map((route, i) => (
                <div key={i} className="flex items-center justify-between bg-[#f8fafc] rounded-xl px-4 py-3">
                  <span className="text-sm text-[#1e293b] font-medium">{route}</span>
                  <button
                    onClick={() => navigate("/")}
                    className="text-xs font-medium text-[#f97316] hover:underline flex items-center gap-1"
                  >
                    {t("guide.book_now")} <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0f172a] rounded-2xl p-8 text-center my-8">
          <h2 className="font-['Outfit'] text-lg font-bold text-white mb-2">
            {t("guide.cta_title", { country: guide.country_name })}
          </h2>
          <button
            onClick={() => navigate("/")}
            className="mt-4 bg-[#f97316] text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-[#ea580c] transition-all flex items-center gap-2 mx-auto"
            data-testid="guide-cta-btn"
          >
            <Search className="w-4 h-4" /> {t("guide.cta_button")}
          </button>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-[#1e293b] mb-3">{t("guide.related")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {related.map((r) => (
                <Link
                  key={r.country_name}
                  to={`/guides/${r.country_name?.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#f8fafc] rounded-xl p-3 text-center hover:bg-[#f1f5f9] transition-colors"
                  data-testid={`related-${r.country_code}`}
                >
                  <span className="text-2xl block mb-1">{r.flag_emoji}</span>
                  <span className="text-xs font-medium text-[#1e293b]">{r.country_name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
